// client/src/components/CasePosts.tsx - Attorney Case Posting Component
import { useState, useEffect } from 'react';
import { supabase } from '../supabase';

interface CasePost {
    id: string;
    title: string;
    description: string;
    case_type: 'plaintiff' | 'defense' | 'not_specified';
    specialty_needed: string;
    state_needed: string | null;
    urgency: 'standard' | 'urgent' | 'emergency';
    budget_range: string | null;
    timeline: string | null;
    status: 'open' | 'closed' | 'filled';
    is_active: boolean;
    created_at: string;
    response_count?: number;
}

interface CasePostResponse {
    id: string;
    message: string;
    is_anonymous: boolean;
    anonymous_id: string | null;
    read: boolean;
    created_at: string;
    expert_id: string;
    experts?: {
        full_name: string;
        specialty: string;
        email: string;
        phone_number: string;
    };
}

interface SubscriptionStatus {
    has_subscription: boolean;
    plan: string | null;
    posts_used: number;
    posts_limit: number;
    can_post: boolean;
}

const SPECIALTIES = [
    'Anesthesiology', 'Cardiology', 'Dermatology', 'Emergency Medicine',
    'Endocrinology', 'Family Medicine', 'Gastroenterology', 'Internal Medicine',
    'Neurology', 'Nursing', 'Obstetrics & Gynecology', 'Oncology',
    'Orthopedics', 'Pediatrics', 'Psychiatry', 'Radiology',
    'Surgery', 'Therapy', 'Urology'
];

export default function CasePosts() {
    const [posts, setPosts] = useState<CasePost[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedPost, setSelectedPost] = useState<CasePost | null>(null);
    const [responses, setResponses] = useState<CasePostResponse[]>([]);
    const [loadingResponses, setLoadingResponses] = useState(false);
    const [attorney, setAttorney] = useState<any>(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
    const [showContactModal, setShowContactModal] = useState(false);
    const [selectedResponse, setSelectedResponse] = useState<CasePostResponse | null>(null);
    const [revealingIdentity, setRevealingIdentity] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        case_type: 'not_specified' as const,
        specialty_needed: '',
        state_needed: '',
        urgency: 'standard' as const,
        budget_range: '',
        timeline: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        loadAttorneyAndPosts();
    }, []);

    const loadAttorneyAndPosts = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            // Get attorney profile
            const { data: attorneyData } = await supabase
                .from('attorneys')
                .select('*')
                .eq('user_id', user.id)
                .single();

            if (attorneyData) {
                setAttorney(attorneyData);
                await loadPosts(attorneyData.id);
            }

            // Get subscription status
            const { data: subStatus } = await supabase.rpc('get_subscription_status', {
                for_user_id: user.id
            });

            if (subStatus && subStatus.has_subscription !== undefined) {
                setSubscriptionStatus(subStatus);
            } else if (subStatus && subStatus.length > 0) {
                setSubscriptionStatus(subStatus[0]);
            } else {
                setSubscriptionStatus({
                    has_subscription: false,
                    plan: null,
                    posts_used: 0,
                    posts_limit: 0,
                    can_post: false
                });
            }
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadPosts = async (attorneyId: string) => {
        const { data, error } = await supabase
            .from('case_posts')
            .select('*')
            .eq('attorney_id', attorneyId)
            .order('created_at', { ascending: false });

        if (error) {
            console.error('Error loading posts:', error);
            return;
        }

        // Get response counts for each post
        const postsWithCounts = await Promise.all(
            (data || []).map(async (post) => {
                const { count } = await supabase
                    .from('case_post_responses')
                    .select('*', { count: 'exact', head: true })
                    .eq('post_id', post.id);
                return { ...post, response_count: count || 0 };
            })
        );

        setPosts(postsWithCounts);
    };

    const loadResponses = async (postId: string) => {
        setLoadingResponses(true);
        try {
            const { data, error } = await supabase
                .from('case_post_responses')
                .select(`
                    *,
                    experts (
                        full_name,
                        specialty,
                        email,
                        phone_number
                    )
                `)
                .eq('post_id', postId)
                .order('created_at', { ascending: false });

            if (error) throw error;
            setResponses(data || []);

            // Mark unread responses as read
            const unreadIds = (data || []).filter(r => !r.read).map(r => r.id);
            if (unreadIds.length > 0) {
                await supabase
                    .from('case_post_responses')
                    .update({ read: true })
                    .in('id', unreadIds);
            }
        } catch (error) {
            console.error('Error loading responses:', error);
        } finally {
            setLoadingResponses(false);
        }
    };

    const handleCreatePost = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!attorney) return;

        // Check subscription
        if (!subscriptionStatus?.can_post) {
            setMessage('You have reached your monthly post limit. Please upgrade your subscription.');
            return;
        }

        setSubmitting(true);
        setMessage('');

        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            const { error } = await supabase
                .from('case_posts')
                .insert({
                    attorney_id: attorney.id,
                    user_id: user.id,
                    ...formData
                });

            if (error) throw error;

            // Increment post count
            await supabase.rpc('increment_post_count', { attorney_user_id: user.id });

            setMessage('Case post created successfully!');
            setShowCreateModal(false);
            setFormData({
                title: '',
                description: '',
                case_type: 'not_specified',
                specialty_needed: '',
                state_needed: '',
                urgency: 'standard',
                budget_range: '',
                timeline: ''
            });
            await loadPosts(attorney.id);
            await loadAttorneyAndPosts(); // Refresh subscription status
        } catch (error: any) {
            setMessage('Error creating post: ' + error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const closePost = async (postId: string) => {
        if (!window.confirm('Are you sure you want to close this post? Experts will no longer be able to respond.')) {
            return;
        }

        try {
            const { error } = await supabase
                .from('case_posts')
                .update({ status: 'closed', closed_at: new Date().toISOString() })
                .eq('id', postId);

            if (error) throw error;
            await loadPosts(attorney.id);
            if (selectedPost?.id === postId) {
                setSelectedPost({ ...selectedPost, status: 'closed' });
            }
        } catch (error: any) {
            alert('Error closing post: ' + error.message);
        }
    };

    const reopenPost = async (postId: string) => {
        try {
            const { error } = await supabase
                .from('case_posts')
                .update({ status: 'open', closed_at: null })
                .eq('id', postId);

            if (error) throw error;
            await loadPosts(attorney.id);
            if (selectedPost?.id === postId) {
                setSelectedPost({ ...selectedPost, status: 'open' });
            }
        } catch (error: any) {
            alert('Error reopening post: ' + error.message);
        }
    };

    const deletePost = async (postId: string) => {
        if (!window.confirm('Are you sure you want to delete this post? This cannot be undone.')) {
            return;
        }

        try {
            // Delete responses first (FK constraint)
            await supabase
                .from('case_post_responses')
                .delete()
                .eq('post_id', postId);

            const { error } = await supabase
                .from('case_posts')
                .delete()
                .eq('id', postId);
            if (error) throw error;
            await loadPosts(attorney.id);
            setSelectedPost(null);
        } catch (error: any) {
            alert('Error deleting post: ' + error.message);
        }
    };

    const requestIdentityReveal = async (response: CasePostResponse) => {
        setSelectedResponse(response);
        setShowContactModal(true);
    };

    const revealIdentity = async () => {
        if (!selectedResponse || !attorney) return;

        setRevealingIdentity(true);
        try {
            // DO NOT flip is_anonymous — just send a request to the expert
            const { data: expertData } = await supabase
                .from('experts')
                .select('user_id, full_name, email')
                .eq('id', selectedResponse.expert_id)
                .maybeSingle();

            if (expertData) {
                const { data: { user } } = await supabase.auth.getUser();

                // Send request message — DO NOT include attorney contact info
                const { error: msgError } = await supabase.from('messages').insert({
                    attorney_id: attorney.id,
                    expert_id: selectedResponse.expert_id,
                    sender_type: 'attorney',
                    sender_id: user?.id,
                    recipient_id: expertData.user_id,
                    message: `CONTACT INFO REQUEST\n--------------------\nAn attorney has requested your contact information regarding the case posting: "${selectedPost?.title}".\n\nIf you would like to share your identity and contact details, please reply to this message. You can choose to remain anonymous or reveal your identity.\n\nThe attorney's information will be shared with you once you agree to connect.`,
                    case_title: selectedPost?.title || '',
                    case_type: 'not_specified',
                    urgency: 'standard',
                    read: false,
                    is_conflict_notice: false,
                    is_anonymous: false,
                    created_at: new Date().toISOString(),
                });

                if (msgError) {
                    console.error('Message notification failed:', msgError);
                    throw msgError;
                }
            }

            alert('Contact information request sent to the expert. You will be notified when they respond.');

            if (selectedPost) {
                await loadResponses(selectedPost.id);
            }
            setShowContactModal(false);
            setSelectedResponse(null);
        } catch (error: any) {
            alert('Error sending request: ' + error.message);
        } finally {
            setRevealingIdentity(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    // No subscription - show upgrade prompt
    if (!subscriptionStatus?.has_subscription) {
        return (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
                <svg className="w-16 h-16 text-yellow-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <h3 className="text-xl font-bold text-yellow-800 mb-2">Subscription Required</h3>
                <p className="text-yellow-700 mb-4">
                    You need an active subscription to post cases for expert witnesses to find.
                </p>
                <p className="text-sm text-yellow-600">
                    Subscribe to start posting cases and connecting with experts.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Case Posts</h2>
                    <p className="text-gray-600">Post cases to find expert witnesses</p>
                    {subscriptionStatus && subscriptionStatus.posts_limit !== -1 && (
                        <p className="text-sm text-blue-600 mt-1">
                            Posts this month: {subscriptionStatus.posts_used} / {subscriptionStatus.posts_limit}
                        </p>
                    )}
                </div>
                <button
                    onClick={() => {
                        if (!subscriptionStatus?.can_post) {
                            alert('You have reached your monthly post limit. Please upgrade to Unlimited for unlimited posts.');
                            return;
                        }
                        setShowCreateModal(true);
                    }}
                    disabled={!subscriptionStatus?.can_post}
                    className={`px-4 py-2 rounded-lg flex items-center ${
                        subscriptionStatus?.can_post
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create New Post
                </button>
            </div>

            {/* Posts List */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Posts Column - hide on mobile when viewing detail */}
                <div className={`space-y-4 ${selectedPost ? 'hidden lg:block' : ''}`}>
                    <h3 className="font-semibold text-gray-700">Your Posts ({posts.length})</h3>

                    {posts.length === 0 ? (
                        <div className="bg-gray-50 rounded-lg p-8 text-center">
                            <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <p className="text-gray-600">No case posts yet</p>
                            <p className="text-sm text-gray-500 mt-1">Create a post to find expert witnesses</p>
                        </div>
                    ) : (
                        posts.map((post) => (
                            <div
                                key={post.id}
                                onClick={() => {
                                    setSelectedPost(post);
                                    loadResponses(post.id);
                                }}
                                className={`p-4 rounded-lg border cursor-pointer transition-all ${
                                    selectedPost?.id === post.id
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:border-gray-300 bg-white'
                                }`}
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <h4 className="font-semibold text-gray-900">{post.title}</h4>
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                        post.status === 'open'
                                            ? 'bg-green-100 text-green-800'
                                            : post.status === 'filled'
                                                ? 'bg-blue-100 text-blue-800'
                                                : 'bg-gray-100 text-gray-800'
                                    }`}>
                                        {post.status.charAt(0).toUpperCase() + post.status.slice(1)}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-600 line-clamp-2 mb-2">{post.description}</p>
                                <div className="flex items-center justify-between text-xs text-gray-500">
                                    <span>{post.specialty_needed}</span>
                                    <span className="flex items-center">
                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                        </svg>
                                        {post.response_count} responses
                                    </span>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Detail/Responses Column */}
                <div className={`bg-white rounded-lg border border-gray-200 p-4 sm:p-6 ${!selectedPost ? 'hidden lg:block' : ''}`}>
                    {selectedPost ? (
                        <div>
                            <button
                                onClick={() => setSelectedPost(null)}
                                className="lg:hidden mb-4 text-blue-600 text-sm font-medium flex items-center"
                            >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                Back to Posts
                            </button>
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-xl font-bold text-gray-900">{selectedPost.title}</h3>
                                <div className="flex space-x-2">
                                    {selectedPost.status === 'open' ? (
                                        <button
                                            onClick={() => closePost(selectedPost.id)}
                                            className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded text-sm hover:bg-yellow-200"
                                        >
                                            Close Post
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => reopenPost(selectedPost.id)}
                                            className="px-3 py-1 bg-green-100 text-green-800 rounded text-sm hover:bg-green-200"
                                        >
                                            Reopen
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-3 mb-6">
                                <p className="text-gray-700">{selectedPost.description}</p>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-gray-500">Specialty:</span>
                                        <span className="ml-2 font-medium">{selectedPost.specialty_needed}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Case Type:</span>
                                        <span className="ml-2 font-medium capitalize">{selectedPost.case_type.replace('_', ' ')}</span>
                                    </div>
                                    {selectedPost.state_needed && (
                                        <div>
                                            <span className="text-gray-500">State:</span>
                                            <span className="ml-2 font-medium">{selectedPost.state_needed}</span>
                                        </div>
                                    )}
                                    <div>
                                        <span className="text-gray-500">Urgency:</span>
                                        <span className={`ml-2 font-medium capitalize ${
                                            selectedPost.urgency === 'emergency' ? 'text-red-600' :
                                                selectedPost.urgency === 'urgent' ? 'text-orange-600' : ''
                                        }`}>{selectedPost.urgency}</span>
                                    </div>
                                    {selectedPost.budget_range && (
                                        <div>
                                            <span className="text-gray-500">Budget:</span>
                                            <span className="ml-2 font-medium">{selectedPost.budget_range}</span>
                                        </div>
                                    )}
                                    {selectedPost.timeline && (
                                        <div>
                                            <span className="text-gray-500">Timeline:</span>
                                            <span className="ml-2 font-medium">{selectedPost.timeline}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Responses */}
                            <div className="border-t pt-4">
                                <h4 className="font-semibold text-gray-900 mb-4">
                                    Responses ({responses.length})
                                </h4>

                                {loadingResponses ? (
                                    <div className="flex justify-center py-8">
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                    </div>
                                ) : responses.length === 0 ? (
                                    <p className="text-gray-500 text-center py-8">No responses yet</p>
                                ) : (
                                    <div className="space-y-4 max-h-96 overflow-y-auto">
                                        {responses.map((response) => (
                                            <div key={response.id} className="p-4 bg-gray-50 rounded-lg">
                                                <div className="flex justify-between items-start mb-2">
                                                    <div>
                                                        {response.is_anonymous ? (
                                                            <div className="flex items-center space-x-2">
                                                                <span className="font-medium text-gray-700">
                                                                    🔒 Anonymous Expert
                                                                </span>
                                                                <button
                                                                    onClick={() => requestIdentityReveal(response)}
                                                                    className="text-xs text-blue-600 hover:text-blue-700 underline"
                                                                >
                                                                    Request to Connect
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div>
                                                                <span className="font-medium text-gray-900">
                                                                    {response.experts?.full_name}
                                                                </span>
                                                                <span className="text-sm text-gray-500 ml-2">
                                                                    {response.experts?.specialty}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <span className="text-xs text-gray-500">
                                                        {new Date(response.created_at).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                <p className="text-gray-700 text-sm">{response.message}</p>
                                                {!response.is_anonymous && response.experts && (
                                                    <div className="mt-3 pt-3 border-t flex space-x-4 text-sm">
                                                        <a
                                                            href={`mailto:${response.experts.email}`}
                                                            className="text-blue-600 hover:underline flex items-center"
                                                        >
                                                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                            </svg>
                                                            {response.experts.email}
                                                        </a>
                                                        {response.experts.phone_number && (
                                                            <a
                                                                href={`tel:${response.experts.phone_number}`}
                                                                className="text-blue-600 hover:underline flex items-center"
                                                            >
                                                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                                </svg>
                                                                {response.experts.phone_number}
                                                            </a>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                            </svg>
                            <p>Select a post to view details and responses</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Create Post Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-bold text-gray-900">Create Case Post</h3>
                                <button
                                    onClick={() => setShowCreateModal(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm text-blue-800">
                                    <strong>Privacy Note:</strong> Your personal information will NOT be shared with experts.
                                    Only the information you enter below will be visible to verified experts.
                                </p>
                            </div>

                            <form onSubmit={handleCreatePost} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Post Title *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="e.g., Seeking Cardiology Expert for Medical Malpractice Case"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Description *
                                    </label>
                                    <textarea
                                        required
                                        rows={4}
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="Describe the case details, what you're looking for in an expert, timeline, etc. Do NOT include any identifying information about parties involved."
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Specialty Needed *
                                        </label>
                                        <select
                                            required
                                            value={formData.specialty_needed}
                                            onChange={(e) => setFormData({ ...formData, specialty_needed: e.target.value })}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="">Select specialty</option>
                                            {SPECIALTIES.map((s) => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Case Type
                                        </label>
                                        <select
                                            value={formData.case_type}
                                            onChange={(e) => setFormData({ ...formData, case_type: e.target.value as any })}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="not_specified">Not Specified</option>
                                            <option value="plaintiff">Plaintiff</option>
                                            <option value="defense">Defense</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            State Needed
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.state_needed}
                                            onChange={(e) => setFormData({ ...formData, state_needed: e.target.value })}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                            placeholder="e.g., California"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Urgency
                                        </label>
                                        <select
                                            value={formData.urgency}
                                            onChange={(e) => setFormData({ ...formData, urgency: e.target.value as any })}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="standard">Standard</option>
                                            <option value="urgent">Urgent</option>
                                            <option value="emergency">Emergency</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Budget Range
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.budget_range}
                                            onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                            placeholder="e.g., $5,000 - $10,000"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Timeline
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.timeline}
                                            onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                            placeholder="e.g., Trial in 3 months"
                                        />
                                    </div>
                                </div>

                                {message && (
                                    <div className={`p-4 rounded-lg ${
                                        message.includes('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                                    }`}>
                                        {message}
                                    </div>
                                )}

                                <div className="flex justify-end space-x-4 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowCreateModal(false)}
                                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                                    >
                                        {submitting ? 'Creating...' : 'Create Post'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Contact Info Request Modal */}
            {showContactModal && selectedResponse && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Request Expert Contact Information</h3>
                        <p className="text-gray-600 mb-6">
                            This expert responded anonymously. Would you like to request their contact information?
                            The expert will be notified that you want to connect.
                        </p>
                        <div className="flex space-x-4">
                            <button
                                onClick={revealIdentity}
                                disabled={revealingIdentity}
                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                            >
                                {revealingIdentity ? 'Requesting...' : 'Request Contact Info'}
                            </button>
                            <button
                                onClick={() => {
                                    setShowContactModal(false);
                                    setSelectedResponse(null);
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
